import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    AgenteMonedaView,
    CotizacionTemplateDTO,
    CreateCotizacionTemplateRequest,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { hasIncompleteValues, moveByKey, newValueRow, patchByKey, removeByKey, toValueRequests, toValueRows } from '../../../utils/value-rows'
import type { ValueRow } from '../../../utils/value-rows'
import {
    errorMessage,
    fetchCoberturaSuggestions,
    fetchQuoteCatalogs,
    insurerOption,
    PLAN_MAX,
    PLAN_VALUE_MAX,
    planValueFields,
    QUOTE_TEMPLATES_PATH,
    ramoFields,
} from '../common/quote-template-common'
import type { PlanValueField, QuoteInsurerOption, QuoteLocationState } from '../common/quote-template-common'

export const NOMBRE_MAX = 100

const LOAD_ERROR = 'No se pudo cargar el template.'
const CATALOGS_ERROR = 'No se pudieron cargar los catálogos.'
const SAVE_ERROR = 'Error al guardar el template'

export interface QuoteTemplateFormState {
    id: number | null
    nombre: string
    ramo: string
    aseguradora: QuoteInsurerOption | null
    moneda: string
    plan: string
    sumaAsegurada: string
    deducible: string
    coaseguro: string
    topeCoaseguro: string
    copago: string
    caracteristicas: ValueRow[]
    coberturas: ValueRow[]
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

export type TemplateValueListKind = 'caracteristicas' | 'coberturas'

const emptyState: QuoteTemplateFormState = {
    id: null,
    nombre: '',
    ramo: '',
    aseguradora: null,
    moneda: '',
    plan: '',
    sumaAsegurada: '',
    deducible: '',
    coaseguro: '',
    topeCoaseguro: '',
    copago: '',
    caracteristicas: [],
    coberturas: [],
}

const toFormState = (body: CotizacionTemplateDTO): QuoteTemplateFormState => ({
    id: body.id,
    nombre: body.nombre || '',
    ramo: body.ramo || '',
    aseguradora: insurerOption(body.aseguradora),
    moneda: body.moneda?.abreviacion || '',
    plan: body.plan || '',
    sumaAsegurada: body.sumaAsegurada || '',
    deducible: body.deducible || '',
    coaseguro: body.coaseguro || '',
    topeCoaseguro: body.topeCoaseguro || '',
    copago: body.copago || '',
    caracteristicas: toValueRows(body.caracteristicas),
    coberturas: toValueRows(body.coberturas),
})

const toRequest = (state: QuoteTemplateFormState): CreateCotizacionTemplateRequest => {
    const fields = ramoFields(state.ramo)
    return {
        id: state.id,
        nombre: state.nombre.trim(),
        ramo: state.ramo,
        aseguradora: Number(state.aseguradora?.id),
        moneda: state.moneda,
        plan: state.plan.trim(),
        sumaAsegurada: state.sumaAsegurada.trim(),
        deducible: fields.deducible ? state.deducible.trim() || null : null,
        coaseguro: fields.salud ? state.coaseguro.trim() || null : null,
        topeCoaseguro: fields.salud ? state.topeCoaseguro.trim() || null : null,
        copago: fields.salud ? state.copago.trim() || null : null,
        caracteristicas: fields.caracteristicas ? toValueRequests(state.caracteristicas) : [],
        coberturas: toValueRequests(state.coberturas),
    }
}

export const useQuoteTemplateFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { templateId } = useParams()
    const backUrl = (location.state as QuoteLocationState | null)?.backUrl || QUOTE_TEMPLATES_PATH

    const [state, setState] = useState<QuoteTemplateFormState>(emptyState)
    const [aseguradoras, setAseguradoras] = useState<QuoteInsurerOption[]>([])
    const [monedas, setMonedas] = useState<AgenteMonedaView[]>([])
    const [coberturaSuggestions, setCoberturaSuggestions] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)

    const patch = (partial: Partial<QuoteTemplateFormState>) => setState((current) => ({ ...current, ...partial }))

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setMessage(null)
                const catalogs = await fetchQuoteCatalogs()
                if (!active) return
                setAseguradoras(catalogs.aseguradoras.map((item) => insurerOption(item)).filter((item): item is QuoteInsurerOption => Boolean(item)))
                setMonedas(catalogs.monedas)
                if (!templateId) return
                const response = await APIInvoke.invokeGET(`/cotizaciones/templates/${templateId}`)
                const payload: WrapperResponse<CotizacionTemplateDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setState(toFormState(payload.body))
            } catch (error) {
                if (active) setMessage({ type: 'error', message: errorMessage(error, templateId ? LOAD_ERROR : CATALOGS_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [templateId])

    useEffect(() => {
        if (!state.ramo) {
            setCoberturaSuggestions([])
            return undefined
        }
        let active = true
        fetchCoberturaSuggestions(state.ramo)
            .then((items) => { if (active) setCoberturaSuggestions(items) })
            .catch(() => { if (active) setCoberturaSuggestions([]) })
        return () => { active = false }
    }, [state.ramo])

    const valueList = (kind: TemplateValueListKind) => ({
        add: () => setState((current) => ({ ...current, [kind]: [...current[kind], newValueRow()] })),
        change: (key: string, partial: Partial<Pick<ValueRow, 'descripcion' | 'valor'>>) =>
            setState((current) => ({ ...current, [kind]: patchByKey<ValueRow>(current[kind], key, partial) })),
        remove: (key: string) => setState((current) => ({ ...current, [kind]: removeByKey(current[kind], key) })),
        move: (key: string, delta: -1 | 1) => setState((current) => ({ ...current, [kind]: moveByKey(current[kind], key, delta) })),
    })

    const change = {
        nombre: (value: string) => patch({ nombre: clip(value, NOMBRE_MAX) }),
        ramo: (ramo: string) => {
            if (state.id) return
            patch({ ramo, coberturas: [] })
        },
        aseguradora: (aseguradora: QuoteInsurerOption | null) => patch({ aseguradora }),
        moneda: (value: string) => patch({ moneda: value }),
        plan: (value: string) => patch({ plan: clip(value, PLAN_MAX) }),
        field: (field: PlanValueField, value: string) => patch({ [field]: clip(value, PLAN_VALUE_MAX) }),
    }

    const fields = ramoFields(state.ramo)

    const validate = () => {
        if (!state.nombre.trim()) return 'Captura el nombre del template.'
        if (!state.ramo) return 'Elige el ramo.'
        if (!state.aseguradora?.id) return 'Elige la aseguradora.'
        if (!state.moneda) return 'Elige la moneda.'
        if (!state.plan.trim()) return 'Captura el plan o paquete.'
        if (!state.sumaAsegurada.trim()) return 'Captura la suma asegurada.'
        if (fields.caracteristicas && hasIncompleteValues(state.caracteristicas)) return 'Completa descripción y valor de cada característica.'
        if (hasIncompleteValues(state.coberturas)) return 'Completa descripción y valor de cada cobertura.'
        return null
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const invalid = validate()
        if (invalid) {
            setMessage({ type: 'error', message: invalid })
            return
        }
        try {
            setLoading(true)
            setMessage(null)
            const request = toRequest(state)
            const response: WrapperResponse<CotizacionTemplateDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/cotizaciones/templates/${state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/cotizaciones/templates', request)).json()

            if (response.ok && response.body) {
                const nextState: QuoteLocationState = { detailId: response.body.id, backUrl: QUOTE_TEMPLATES_PATH }
                history.push({ pathname: QUOTE_TEMPLATES_PATH, state: nextState })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el template', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const isEdit = Boolean(templateId || state.id)

    return {
        form: state,
        fields,
        planFields: planValueFields(state.ramo),
        aseguradoras,
        monedas,
        coberturaSuggestions,
        loading,
        busyLabel: templateId && !state.id ? 'Cargando template…' : 'Guardando template…',
        message,
        title: isEdit ? 'Editar template' : 'Nuevo template',
        isEdit,
        backTo: backUrl,
        change,
        caracteristicas: valueList('caracteristicas'),
        coberturas: valueList('coberturas'),
        save,
        cancel: () => history.push(backUrl),
    }
}
